<script>

  import { apiFetch, isLoggedIn, user } from '../lib/auth.js';
  import ManageArtists from './ManageArtists.svelte';
  import ManageVenues  from './ManageVenues.svelte';
  import ManageEvents  from './ManageEvents.svelte';
  import AdminUsers    from './AdminUsers.svelte';
  import AdminNews     from './AdminNews.svelte';

  // ── Admin detection ────────────────────────────────────────────────
  let isAdmin  = false;
  let meLoaded = false;

  async function loadMe() {
    if (meLoaded || !$isLoggedIn) return;
    meLoaded = true;
    try {
      const me = await apiFetch('/users/me');
      isAdmin = Array.isArray(me.roles) && me.roles.includes('admin');
    } catch {}
  }

  $: if ($isLoggedIn) loadMe();

  // ── Tabs ───────────────────────────────────────────────────────────
  const BASE_TABS = [
    { key: 'artists', label: 'Artists' },
    { key: 'venues',  label: 'Venues'  },
    { key: 'events',  label: 'Events'  },
  ];

  $: TABS = isAdmin
    ? [...BASE_TABS,
       { key: 'users', label: 'Users', admin: true },
       { key: 'news',  label: 'News',  admin: true }]
    : BASE_TABS;

  let activeTab = 'artists';
  $: if ((activeTab === 'users' || activeTab === 'news') && !isAdmin) activeTab = 'artists';

  // ── Per-tab record caches ──────────────────────────────────────────
  // Records are re-fetched on every tab switch so lists stay fresh after
  // adds/edits made in other tabs. Child components own their form/UI state.
  let records  = { artists: [], venues: [], events: [], users: [] };
  let tabLoad  = { artists: false, venues: false, events: false, users: false };
  let tabError = { artists: '',    venues: '',    events: '',    users: '' };

  async function loadTab(tab) {
    if (!Object.hasOwn(records, tab)) return;
    if (tabLoad[tab]) return;  // already in-flight, don't double-fetch
    tabLoad[tab] = true; tabError[tab] = '';
    try {
      if (tab === 'users') {
        const data = await apiFetch('/admin/users');
        records[tab] = Array.isArray(data) ? data : (data.users || []);
      } else {
        const data = await apiFetch(`/${tab}?limit=200`);
        records[tab] = Array.isArray(data) ? data : (data[tab] || data.items || []);
      }
    } catch (e) {
      tabError[tab] = e.message || 'Failed to load';
    } finally {
      tabLoad[tab] = false;
    }
  }

  // Re-fetch every time the active tab changes — keeps lists fresh after
  // adds/edits in other tabs without requiring a full page reload.
  $: loadTab(activeTab);
</script>

<div class="contribute">

  <!-- Tab bar -->
  <div class="tab-bar">
    {#each TABS as t}
      <button
        class="tab"
        class:active={activeTab === t.key}
        class:admin-tab={t.admin}
        on:click={() => { activeTab = t.key; }}
      >
        {#if t.admin}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tab-icon">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
        {/if}
        {t.label}
      </button>
    {/each}
  </div>

  {#if !$isLoggedIn}
    <div class="notice">Log in to contribute.</div>

  {:else}
    <div class="tab-content">

      {#if tabLoad[activeTab]}
        <div class="state-msg"><span class="spinner"></span> Loading...</div>

      {:else if tabError[activeTab]}
        <div class="state-error">{tabError[activeTab]}</div>

      {:else if activeTab === 'artists'}
        <ManageArtists bind:records={records.artists} />

      {:else if activeTab === 'venues'}
        <ManageVenues bind:records={records.venues} />

      {:else if activeTab === 'events'}
        <ManageEvents bind:records={records.events} />

      {:else if activeTab === 'users' && isAdmin}
        <!-- Refresh clears the cache so loadTab re-fetches on next tick -->
        <AdminUsers
          bind:records={records.users}
          on:refresh={() => { records.users = []; loadTab('users'); }}
        />

      {:else if activeTab === 'news' && isAdmin}
        <AdminNews />

      {/if}

    </div>
  {/if}

</div>
